import React, { useMemo } from 'react';

import { StyleSheet, View } from 'react-native';
import { Icon } from 'ucl/components/icon/Icon';

import { ProductDetailListItem } from 'components/product-detail-list-item';
import { ProductDetailListItemGroup } from 'components/product-detail-list-item-group';
import { getCatalogComboSlotProducts } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-catalog-preview/get-catalog-included';
import { useFormatCalories } from 'hooks/use-format-calories';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { isCombo, mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import { spacing } from 'styles/constants';

type CatalogComboSlotsSectionProps = {
  menu: IndexedMenu<ILocalizedMenuEntry>;
  product: ILocalizedMenuEntry;
  /** Called when a combo slot product is pressed — prompt restaurant selection. */
  onRequireRestaurantSelection: () => void;
};

/**
 * No-store combo preview: list every product option across combo slots.
 * Presses do not open customization — they nudge the Select Restaurant CTA instead.
 */
export function CatalogComboSlotsSection({
  menu,
  product,
  onRequireRestaurantSelection,
}: CatalogComboSlotsSectionProps) {
  const formatCalories = useFormatCalories();

  const items = useMemo(() => {
    if (!isCombo(product)) {
      return [];
    }
    return getCatalogComboSlotProducts(menu, product);
  }, [menu, product]);

  if (items.length === 0) {
    return null;
  }

  return (
    <View style={styles.container} testID="menu-catalog-combo-slots">
      <ProductDetailListItemGroup
        items={items.map(item => {
          const name = item.name?.locale ?? item.id;
          const calories = item.nutrition?.calories?.def ?? 0;
          const imgSrc = item.image?.resource;
          const image = imgSrc ? mapImageResourceToSanityImage(imgSrc) : undefined;

          return (
            <ProductDetailListItem
              key={item.id}
              isClickable
              onNavigate={onRequireRestaurantSelection}
              productProps={{
                name,
                calories: formatCalories(calories),
                image: image ? JSON.stringify(image) : undefined,
                isProductNameBold: true,
              }}
              controls={<Icon name="custom:Next" testID="catalog-combo-slot-next-icon" />}
            />
          );
        })}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: spacing.$2,
    width: '100%',
  },
});
